import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { fetchPlans, clearPlansError } from '../../features/subscription/plansSlice';
import {
  groupPlansByCode,
  filterPlanGroupsForInterval,
  formatCreditPackPrice,
  normalizeBillingInterval,
  partitionCatalogPlans,
  sortPlanGroups,
} from '../../utils/plansCatalog';
import PlanPricingCard from './PlanPricingCard';
import CreditPackPurchaseModal from './CreditPackPurchaseModal';

const MARKETING_PRICING_URL = 'https://volksign.com/pricing-plans/';

const normalizeCatalogTab = (value) => {
  const raw = String(value || '').toLowerCase();
  if (raw === 'credit_packs' || raw === 'credit_pack' || raw === 'credits' || raw === 'packs') {
    return 'credit_packs';
  }
  return 'subscriptions';
};

const PlansPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, status, error } = useSelector((state) => state.plans);
  const currentPlanCode = useSelector(
    (state) => state.subscription?.userSubscription?.planCode
  );

  const initialInterval = normalizeBillingInterval(searchParams.get('interval') || 'month');
  const [billingInterval, setBillingInterval] = useState(initialInterval);
  const [selectedPack, setSelectedPack] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState('');

  const initialTab = normalizeCatalogTab(
    searchParams.get('tab')
    || (String(searchParams.get('plan') || '').toLowerCase() === 'credit_pack'
      ? 'credit_packs'
      : 'subscriptions')
  );
  const [catalogTab, setCatalogTab] = useState(initialTab);

  useEffect(() => {
    // Public pricing: hide internal `unlimited` plan (API may still return it).
    dispatch(fetchPlans({ hideUnlimited: true, hideLegacy: true }));
    return () => dispatch(clearPlansError());
  }, [dispatch]);

  const { subscription: subscriptionPlans, creditPacks: creditPackPlans } = useMemo(
    () => partitionCatalogPlans(items),
    [items]
  );

  const planGroups = useMemo(
    () => sortPlanGroups(groupPlansByCode(subscriptionPlans)),
    [subscriptionPlans]
  );

  const visiblePlanGroups = useMemo(
    () => filterPlanGroupsForInterval(planGroups, billingInterval),
    [planGroups, billingInterval]
  );

  const creditPackPlan = creditPackPlans[0] || null;
  const creditPackRows = useMemo(() => {
    if (!creditPackPlan) return [];
    if (Array.isArray(creditPackPlan.creditPacks) && creditPackPlan.creditPacks.length > 0) {
      return creditPackPlan.creditPacks;
    }
    const rawPacks = creditPackPlan.raw?.credit_packs || creditPackPlan.credit_packs || [];
    if (!Array.isArray(rawPacks)) return [];
    return rawPacks.map((pack) => {
      const price = Number(pack.price);
      const amountCents = Number(pack.amount_cents ?? pack.amountCents);
      const hasPrice =
        (Number.isFinite(price) && price > 0)
        || (Number.isFinite(amountCents) && amountCents > 0);
      const flag = pack.purchase_available ?? pack.purchaseAvailable;
      const parentFlag = creditPackPlan.purchaseAvailable;
      const purchaseAvailable =
        flag === true || flag === 1 || flag === '1' || String(flag).toLowerCase() === 'true'
        || parentFlag === true
        || (flag == null && parentFlag == null && hasPrice);
      return {
        code: pack.code ?? null,
        envelopes: pack.envelopes ?? null,
        price: Number.isFinite(price) ? price : null,
        amountCents: Number.isFinite(amountCents) ? amountCents : null,
        currency: String(pack.currency || creditPackPlan.currency || 'usd').toLowerCase(),
        label: pack.label ?? pack.name ?? pack.code ?? 'Credit pack',
        purchaseAvailable,
        raw: pack,
      };
    });
  }, [creditPackPlan]);
  // Show Credit packs tab whenever API returns a credit-pack plan (even before rows hydrate).
  const hasCreditPacks = creditPackPlans.length > 0;

  useEffect(() => {
    if (status === 'succeeded' && catalogTab === 'credit_packs' && !hasCreditPacks) {
      setCatalogTab('subscriptions');
    }
  }, [status, catalogTab, hasCreditPacks]);

  const planParam = searchParams.get('plan');
  const intervalParam = searchParams.get('interval');

  useEffect(() => {
    if (planParam && intervalParam && status === 'succeeded') {
      const code = String(planParam).toLowerCase();
      if (code === 'credit_pack') return;
      navigate(
        `/plans/checkout?plan=${encodeURIComponent(planParam)}&interval=${encodeURIComponent(intervalParam)}`,
        { replace: true }
      );
    }
  }, [planParam, intervalParam, status, navigate]);

  const handleCatalogTabChange = (nextTab) => {
    const tab = normalizeCatalogTab(nextTab);
    setCatalogTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'credit_packs') next.set('tab', 'credit_packs');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  const handleSelectPlan = (group, interval) => {
    if (String(group?.code || '').toLowerCase() === 'credit_pack') return;
    navigate(
      `/plans/checkout?plan=${encodeURIComponent(group.code)}&interval=${encodeURIComponent(interval)}`
    );
  };

  const handlePurchasePack = (pack) => {
    if (!pack?.code) return;
    setPurchaseSuccess('');
    setSelectedPack({
      ...pack,
      purchaseAvailable: pack.purchaseAvailable !== false,
    });
  };

  const isCurrentGroup = (group) =>
    group.isCurrent
    || (currentPlanCode && group.code === String(currentPlanCode).toLowerCase());

  return (
    <div className="plans-page">
      <section className="plans-page__hero">
        <p className="plans-page__eyebrow">Pricing plan</p>
        <h1 className="plans-page__headline">Flexible plans that scale as you grow</h1>
        <p className="plans-page__lede">
          Choose a subscription or buy envelope credits as you need them. All prices in USD.
        </p>

        {hasCreditPacks && (
          <div className="plans-page__toggle plans-page__catalog-tabs" role="tablist" aria-label="Plan type">
            <button
              type="button"
              role="tab"
              aria-selected={catalogTab === 'subscriptions'}
              className={[
                'plans-page__toggle-btn',
                catalogTab === 'subscriptions' ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleCatalogTabChange('subscriptions')}
            >
              Subscriptions
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={catalogTab === 'credit_packs'}
              className={[
                'plans-page__toggle-btn',
                catalogTab === 'credit_packs' ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleCatalogTabChange('credit_packs')}
            >
              Credit packs
            </button>
          </div>
        )}

        {catalogTab === 'subscriptions' && (
          <div
            className={[
              'plans-page__toggle',
              hasCreditPacks ? 'plans-page__toggle--secondary' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="tablist"
            aria-label="Billing period"
          >
            <button
              type="button"
              role="tab"
              aria-selected={billingInterval === 'month'}
              className={[
                'plans-page__toggle-btn',
                billingInterval === 'month' ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setBillingInterval('month')}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billingInterval === 'year'}
              className={[
                'plans-page__toggle-btn',
                billingInterval === 'year' ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setBillingInterval('year')}
            >
              Annual
              <span className="plans-page__toggle-save">Save vs monthly</span>
            </button>
          </div>
        )}
      </section>

      {status === 'loading' && items.length === 0 && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {error && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span>{error}</span>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => dispatch(fetchPlans({ hideUnlimited: true, hideLegacy: true }))}
          >
            Retry
          </Button>
        </Alert>
      )}

      {purchaseSuccess && (
        <Alert variant="success" dismissible onClose={() => setPurchaseSuccess('')}>
          {purchaseSuccess}
        </Alert>
      )}

      {status === 'succeeded' && planGroups.length === 0 && creditPackRows.length === 0 && (
        <Alert variant="info">No plans are available right now.</Alert>
      )}

      {catalogTab === 'subscriptions' && (
        <>
          {status === 'succeeded' && planGroups.length > 0 && visiblePlanGroups.length === 0 && (
            <Alert variant="info">
              No {billingInterval === 'year' ? 'annual' : 'monthly'} price is configured for these plans.
            </Alert>
          )}

          {visiblePlanGroups.length > 0 && (
            <div className="plans-page__grid">
              {visiblePlanGroups.map((group) => (
                <PlanPricingCard
                  key={`${group.code}-${billingInterval}`}
                  group={group}
                  billingInterval={billingInterval}
                  isCurrent={isCurrentGroup(group)}
                  onSelect={handleSelectPlan}
                />
              ))}
            </div>
          )}
        </>
      )}

      {catalogTab === 'credit_packs' && hasCreditPacks && (
        <section className="plans-page__credit-packs">
          <div className="text-center mb-3">
            <h2 className="h4 fw-semibold mb-1">
              {creditPackPlan?.name || 'Credit packs'}
            </h2>
            <p className="text-muted mb-0">
              {creditPackPlan?.tagline
                || creditPackPlan?.description
                || 'Pay-as-you-go flexibility'}
            </p>
          </div>
          {creditPackRows.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No credit packs are configured yet.
            </Alert>
          ) : (
            <Table responsive hover className="plans-page__credit-table align-middle">
              <thead>
                <tr>
                  <th>Pack</th>
                  <th>Envelopes</th>
                  <th>Price</th>
                  <th className="text-end"> </th>
                </tr>
              </thead>
              <tbody>
                {creditPackRows.map((pack) => {
                  const canPurchase = pack.purchaseAvailable !== false
                    && Boolean(pack.code)
                    && (
                      pack.purchaseAvailable === true
                      || creditPackPlan?.purchaseAvailable === true
                      || (pack.price != null && Number(pack.price) > 0)
                      || (pack.amountCents != null && Number(pack.amountCents) > 0)
                    );
                  return (
                    <tr key={pack.code || pack.label}>
                      <td className="fw-medium">{pack.label}</td>
                      <td>{pack.envelopes ?? '—'}</td>
                      <td>
                        {formatCreditPackPrice(
                          pack,
                          (pack.currency || creditPackPlan?.currency || 'usd').toUpperCase()
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant={canPurchase ? 'primary' : 'outline-secondary'}
                          disabled={!canPurchase}
                          title={canPurchase ? `Purchase ${pack.label}` : 'Not available'}
                          onClick={() => handlePurchasePack({
                            ...pack,
                            purchaseAvailable: true,
                            currency: pack.currency || creditPackPlan?.currency || 'usd',
                          })}
                        >
                          Purchase Credits
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          <p className="text-muted small text-center mb-0 mt-2">
            Credits never expire and work on Free or any paid plan after included monthly envelopes are used.
          </p>
          {creditPackPlan?.features?.length > 0 && (
            <ul className="plans-page__credit-features list-unstyled d-flex flex-wrap justify-content-center gap-3 mt-3 mb-0 small text-muted">
              {creditPackPlan.features.map((feature, idx) => (
                <li key={idx}>
                  <i className="ri-check-line me-1" aria-hidden />
                  {typeof feature === 'string' ? feature : feature?.name ?? String(feature)}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <CreditPackPurchaseModal
        show={Boolean(selectedPack)}
        pack={selectedPack}
        onHide={() => setSelectedPack(null)}
        onPurchaseComplete={() => {
          setPurchaseSuccess(
            selectedPack
              ? `${selectedPack.label || 'Credit pack'} purchased. Prepaid credits are ready to use.`
              : 'Credit pack purchased successfully.'
          );
          setSelectedPack(null);
        }}
      />

      <section className="plans-page__footer-cta">
        <p className="mb-2 text-muted">
          Need the full feature comparison table?
        </p>
        <Button
          as="a"
          href={MARKETING_PRICING_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="link"
          className="p-0"
        >
          Compare all features on volksign.com
          <i className="ri-external-link-line ms-1" aria-hidden />
        </Button>
        <div className="mt-3">
          <Button as={Link} to="/settings/billing" variant="outline-secondary" size="sm">
            Billing &amp; usage
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PlansPage;
